import {
    isProtectedDocumentFolderPath,
    translateDocument,
    translateDocumentCategory,
    type DocumentCategory,
    type DocumentType,
    type IBaseDocument,
    type IBaseDocumentFolder,
    type IDataRoom,
    type IDocument,
    type IDocumentFolder,
} from '@condo/domain';
import { groupBy, mapValues, partition } from 'lodash-es';
import type { TreeOption } from 'naive-ui/lib';

export interface IVirtualFolder {
    virtualDocumentCategory?: DocumentCategory;
    virtualDocumentType?: DocumentType;
}

export interface IDocumentTreeNodeBase {
    filename: string;
    documentId: number;
    selectableId: string;
    folderId?: number | null;
    isFolder: boolean;
    mimeType?: string;
    link?: string;
    path?: string;
    id: string;
    document?: IBaseDocument;
    folder?: IBaseDocumentFolder & IVirtualFolder;
    totalFiles?: number;
    folderPath?: string;
}

export interface IDocumentTreeNode extends IDocumentTreeNodeBase, TreeOption {
    children: Array<IDocumentTreeNode>;
}

export interface ISimpleDocumentTreeNode extends IDocumentTreeNodeBase {
    children: Array<ISimpleDocumentTreeNode>;
}

export const selectableDocument = (documentId: number) => `document-${documentId}`;
export const selectableFolder = (folderId: number) => `folder-${folderId}`;

export const getSharedFolderOrDocumentLookupMap = (dataRooms: IDataRoom[]) => {
    return dataRooms.reduce(
        (lookupMap, dataRoom) => {
            // Add folders
            dataRoom.folderIds.forEach(id => {
                lookupMap[`folder-${id}`] = true;
            });

            // Add documents
            dataRoom.documentIds.forEach(id => {
                lookupMap[`document-${id}`] = true;
            });

            return lookupMap;
        },
        {} as Record<string, boolean>,
    );
};
export const transformDataToTree = (folderList: IDocumentFolder[], documentList: IDocument[]): IDocumentTreeNode[] => {
    const folderMap = new Map<number, IDocumentTreeNode>();

    folderList.forEach(folder => {
        folderMap.set(folder.folderId, {
            filename: folder.folderName,
            documentId: folder.folderId,
            selectableId: selectableFolder(folder.folderId),
            id: selectableFolder(folder.folderId),
            folder: folder,
            folderId: folder.folderId,
            isFolder: true,
            children: [],
            totalFiles: 0,
        });
    });

    const topLevel: IDocumentTreeNode[] = [];

    documentList.forEach(doc => {
        if (doc.folderId === null || !folderMap.has(doc.folderId!)) {
            topLevel.push({
                filename: doc.filename,
                documentId: doc.documentId,
                selectableId: selectableDocument(doc.documentId),
                id: selectableDocument(doc.documentId),
                document: doc,
                folderId: doc.folderId,
                mimeType: doc.mimeType,
                link: doc.link,
                path: doc.path,
                isFolder: false,
                children: [],
            });
        } else {
            const folderNode = folderMap.get(doc.folderId!);
            folderNode?.children?.push({
                filename: doc.filename,
                documentId: doc.documentId,
                selectableId: selectableDocument(doc.documentId),
                id: selectableDocument(doc.documentId),
                document: doc,
                folderId: doc.folderId,
                mimeType: doc.mimeType,
                link: doc.link,
                path: doc.path,
                isFolder: false,
                children: [],
            });
        }
    });

    folderList.forEach(folder => {
        const folderNode = folderMap.get(folder.folderId);
        if (folder.parentFolderId !== null) {
            const parentNode = folderMap.get(folder.parentFolderId);
            if (parentNode && folderNode) {
                parentNode.children?.push(folderNode);
            }
        } else {
            if (folderNode) {
                topLevel.push(folderNode);
            }
        }
    });

    const sortTreeCountFiles = (nodes: IDocumentTreeNode[]) => {
        nodes.sort((a, b) => {
            if (a.isFolder !== b.isFolder) {
                return b.isFolder ? 1 : -1;
            }

            return a.filename.localeCompare(b.filename);
        });

        nodes.forEach(node => {
            if (node.children) {
                sortTreeCountFiles(node.children);

                if (node.isFolder) {
                    let totalFiles = 0;
                    node.children.forEach(child => {
                        if (child.isFolder) {
                            totalFiles += child.totalFiles || 0;
                        } else {
                            totalFiles += 1;
                        }
                    });
                    node.totalFiles = totalFiles;
                }
            }
        });
    };

    sortTreeCountFiles(topLevel);

    return topLevel;
};

export const transformDataToCategoryTree = (documentList: IDocument[]): IDocumentTreeNode[] => {
    const [documentListNew, orphanDocument] = partition(documentList, doc => !!doc.documentCategory);

    const groupedByCategory = groupBy(documentListNew, 'documentCategory');
    const groupedDocument: Record<string, Record<string, IDocument[]>> = mapValues(groupedByCategory, docs => groupBy(docs, 'documentType'));

    const hierarchialNodes = Object.entries(groupedDocument).map(([categoryName, documentTypes]) => {
        const documentTypeNodes = Object.entries(documentTypes).map(([documentType, documents]) => {
            const documentsNode = documents.map(doc => {
                return {
                    filename: doc.filename,
                    documentId: doc.documentId,
                    selectableId: selectableDocument(doc.documentId),
                    id: selectableDocument(doc.documentId),
                    document: doc,
                    folderId: doc.folderId,
                    mimeType: doc.mimeType,
                    link: doc.link,
                    path: doc.path,
                    isFolder: false,
                    children: [],
                };
            });

            const documentTypeNode: IDocumentTreeNode = {
                filename: documentType,
                selectableId: `document-type-${documentType}`,
                documentId: 0,
                id: `document-type-${documentType}`,
                folder: {
                    folderName: documentType,
                    folderId: 0,
                    parentFolderId: 0,
                    virtualDocumentType: documentType as DocumentType,
                },
                folderId: 0,
                isFolder: true,
                children: documentsNode,
                totalFiles: documentsNode.length,
            };

            return documentTypeNode;
        });

        const categoryNode = {
            filename: categoryName,
            selectableId: `category-${categoryName}`,
            documentId: 0,
            id: `category-${categoryName}`,
            folder: {
                folderName: categoryName,
                folderId: 0,
                parentFolderId: 0,
                virtualDocumentCategory: categoryName as DocumentCategory,
            },
            folderId: 0,
            isFolder: true,
            children: documentTypeNodes,
            totalFiles: documentTypeNodes.reduce((sum, node) => sum + (node.totalFiles || 0), 0),
        };

        return categoryNode;
    });

    return [
        ...hierarchialNodes,
        ...orphanDocument.map(doc => {
            return {
                filename: doc.filename,
                documentId: doc.documentId,
                selectableId: selectableDocument(doc.documentId),
                id: selectableDocument(doc.documentId),
                document: doc,
                folderId: doc.folderId,
                mimeType: doc.mimeType,
                link: doc.link,
                path: doc.path,
                isFolder: false,
                children: [],
            };
        }),
    ];
};

/** User-facing label for category/type virtual folders and physical folders. */
export const getDocumentTreeNodeDisplayName = (node: IDocumentTreeNode): string => {
    if (node.folder?.virtualDocumentType) {
        return translateDocument(node.folder.virtualDocumentType);
    }
    if (node.folder?.virtualDocumentCategory) {
        return translateDocumentCategory(node.folder.virtualDocumentCategory);
    }
    return node.filename ?? node.folder?.folderName ?? '';
};

function createVirtualDocumentTypeNode(documentType: DocumentType): IDocumentTreeNode {
    return {
        filename: documentType,
        selectableId: `document-type-${documentType}`,
        documentId: 0,
        id: `document-type-${documentType}`,
        folder: {
            folderName: documentType,
            folderId: 0,
            parentFolderId: 0,
            virtualDocumentType: documentType,
        },
        folderId: 0,
        isFolder: true,
        children: [],
        totalFiles: 0,
    };
}

function createVirtualCategoryNode(categoryName: DocumentCategory, children: IDocumentTreeNode[]): IDocumentTreeNode {
    return {
        filename: categoryName,
        selectableId: `category-${categoryName}`,
        documentId: 0,
        id: `category-${categoryName}`,
        folder: {
            folderName: categoryName,
            folderId: 0,
            parentFolderId: 0,
            virtualDocumentCategory: categoryName,
        },
        folderId: 0,
        isFolder: true,
        children,
        totalFiles: children.reduce((sum, node) => sum + (node.totalFiles ?? 0), 0),
    };
}

function countFolderFiles(children: IDocumentTreeNode[]): number {
    return children.reduce((sum, child) => sum + (child.isFolder ? (child.totalFiles ?? 0) : 1), 0);
}

/**
 * Ensures every document type in `categoryMap` appears under its category, including empty folders.
 * Used for unit/estate category-grouped document trees (matches ProcessDocumentsBox).
 */
export const enrichCategoryTreeWithAllDocumentTypes = (
    tree: IDocumentTreeNode[],
    categoryMap: Partial<Record<DocumentCategory, DocumentType[]>>,
    allowedCategories: DocumentCategory[],
): IDocumentTreeNode[] => {
    const orphanNodes = tree.filter(node => !node.folder?.virtualDocumentCategory);
    const categoryNodes = tree.filter(node => node.folder?.virtualDocumentCategory);
    const categoryByName = new Map<DocumentCategory, IDocumentTreeNode>(categoryNodes.map(node => [node.folder!.virtualDocumentCategory!, node]));

    const enrichedCategories: IDocumentTreeNode[] = [];

    for (const category of allowedCategories) {
        const documentTypes = categoryMap[category] ?? [];
        if (documentTypes.length === 0) {
            continue;
        }

        const existing = categoryByName.get(category);
        const typeChildren = existing ? [...existing.children] : [];
        const existingTypes = new Set(typeChildren.filter(child => child.folder?.virtualDocumentType).map(child => child.folder!.virtualDocumentType!));

        for (const documentType of documentTypes) {
            if (!existingTypes.has(documentType)) {
                typeChildren.push(createVirtualDocumentTypeNode(documentType));
            }
        }

        typeChildren.sort((a, b) => getDocumentTreeNodeDisplayName(a).localeCompare(getDocumentTreeNodeDisplayName(b), 'de', { numeric: true }));

        enrichedCategories.push(
            existing ? { ...existing, children: typeChildren, totalFiles: countFolderFiles(typeChildren) } : createVirtualCategoryNode(category, typeChildren),
        );
    }

    for (const node of categoryNodes) {
        const category = node.folder?.virtualDocumentCategory;
        if (category && !allowedCategories.includes(category)) {
            enrichedCategories.push(node);
        }
    }

    enrichedCategories.sort((a, b) => getDocumentTreeNodeDisplayName(a).localeCompare(getDocumentTreeNodeDisplayName(b), 'de', { numeric: true }));

    return [...orphanNodes, ...enrichedCategories];
};

const getNodeFolderName = (node: IDocumentTreeNode): string => node.folder?.folderName ?? node.filename;

const getFolderPathLabel = (path: ReadonlyArray<IDocumentTreeNode>): string => path.map(getNodeFolderName).join('/');

export const isDocumentTreePathProtected = (path: ReadonlyArray<IDocumentTreeNode>): boolean => isProtectedDocumentFolderPath(getFolderPathLabel(path));

export const findPathToItem = (items: IDocumentTreeNode[], id: string, path: IDocumentTreeNode[]): IDocumentTreeNode[] => {
    for (const item of items) {
        if (item.id === id) {
            return [...path, item];
        }
        if (item.children) {
            const found = findPathToItem(item.children, id, [...path, item]);
            if (found.length > 0) {
                return found;
            }
        }
    }
    return [];
};

export const findParentPath = (items: IDocumentTreeNode[], id: string, path: IDocumentTreeNode[]): IDocumentTreeNode[] => {
    for (const item of items) {
        if (item.children) {
            if (item.children.some(child => child.selectableId === id)) {
                return [...path, item];
            }
            const found = findParentPath(item.children, id, [...path, item]);
            if (found.length > 0) {
                return found;
            }
        }
    }
    return [];
};

export const getNodeFolderPath = (node: IDocumentTreeNode, documentTree: IDocumentTreeNode[]): IDocumentTreeNode[] => {
    if (node.isFolder) {
        return findPathToItem(documentTree, node.selectableId, []);
    }

    return findParentPath(documentTree, node.selectableId, []);
};

export const isNodeInProtectedDocumentFolder = (node: IDocumentTreeNode, documentTree: IDocumentTreeNode[]): boolean =>
    isDocumentTreePathProtected(getNodeFolderPath(node, documentTree));

const containsProtectedFolder = (node: IDocumentTreeNode, folderPath: IDocumentTreeNode[]): boolean => {
    if (!node.isFolder) {
        return false;
    }

    if (isDocumentTreePathProtected(folderPath)) {
        return true;
    }

    return node.children.some(child => child.isFolder && containsProtectedFolder(child, [...folderPath, child]));
};

export const hasProtectedDocumentFolderChange = (nodes: IDocumentTreeNode[], documentTree: IDocumentTreeNode[]): boolean =>
    nodes.some(node => {
        if (isNodeInProtectedDocumentFolder(node, documentTree)) {
            return true;
        }

        const nodePath = getNodeFolderPath(node, documentTree);
        return containsProtectedFolder(node, nodePath.length ? nodePath : [node]);
    });
